import cn from "classnames";
import { useFieldControl } from "@/common/hooks/useFieldControl";
import { useId } from "@/common/hooks/useId";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";

import styles from "./text-input.module.scss";

/**
 *
 * @typedef {object} Props
 * @prop {string} name - input name
 * @prop {string} className - A className, used to override
 * @prop {string} type - text, password or number
 * @prop {bool} disabled - if the input is disabled
 * @prop {bool} required - if the input is required for the parent form to complete
 * @prop {string} label - label above the input
 * @prop {string} hint - a hint that appears beneath the input. eg. password criteria
 * @prop {TextInput.VALIDATION.PASS | TextInput.VALIDATION.FAIL} [validation] - visual changes based on whether the validation passed or failed
 * @prop {bool} readOnly - if the input is readOnly
 * @prop {string | number} value - text content within the input field
 * @prop {string | number} defaultValue - value on Render -> only used when value is not supplied and the component is uncontrolled
 * @prop {func} onChange - handler that contains the current value for use in parent component
 * @prop {string | number} placeholder - displayed in the input field until the user enters something
 * @prop {number} maxLength - the maximum length of chars
 * @prop {func} ref - ref to access the inner input. Needed to access the value when uncontrolled
 * @prop {func} wrapperRef - ref to access by parent component
 * @prop {string} [id] - an optional id to use instead of the internally generated one
 * @prop {object} [wrapperProps] - an optional props object that will be spreadded into the wrapper component
 *
 * @returns {JSX.Element}
 */
const TextInput = forwardRef(
  (
    {
      className,
      name,
      type,
      disabled,
      required,
      label,
      readOnly,
      validation,
      hint,
      value,
      defaultValue,
      onChange,
      placeholder,
      maxLength,
      wrapperRef,
      id,
      wrapperProps,
      ...props
    },
    ref
  ) => {
    const computedId = useId(name);
    const { fieldControlProps, handleChange } = useFieldControl({
      onChange,
      value,
      defaultValue,
    });

    return (
      <div
        {...wrapperProps}
        ref={wrapperRef}
        className={cn(className, styles.textInputWrapper)}
      >
        {hint && <div className={styles.hint}>{hint}</div>}
        <input
          {...props}
          id={id || computedId}
          readOnly={readOnly}
          maxLength={maxLength}
          type={type}
          required={required}
          name={name}
          disabled={disabled}
          aria-label={name}
          placeholder={placeholder}
          onChange={handleChange}
          ref={ref}
          {...fieldControlProps}
          className={cn({
            [styles.validationPass]: validation === TextInput.VALIDATION.PASS,
            [styles.validationFail]: validation === TextInput.VALIDATION.FAIL,
          })}
        />
        {label && <label htmlFor={id || computedId}>{label}</label>}
      </div>
    );
  }
);

TextInput.defaultProps = {
  type: "text",
  wrapperProps: {},
};

TextInput.VALIDATION = {
  FAIL: "FAIL",
  PASS: "PASS",
};

TextInput.displayName = "TextInput";

TextInput.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["text", "password", "number"]),
  className: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  maxLength: PropTypes.number,
  validation: PropTypes.oneOf([
    TextInput.VALIDATION.FAIL,
    TextInput.VALIDATION.PASS,
  ]),
  hint: PropTypes.string,
  onChange: PropTypes.func,
  wrapperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  id: PropTypes.string,
  wrapperProps: PropTypes.object,
};

export default TextInput;
